import { useEffect, useRef } from "react";
import { usePlayerOccupiedZones } from "core/contexts/PlayerOccupiedZonesContext";

/**
 * Хук для регистрации элемента UI в системе занятых зон
 * @param {string} zoneKey - Ключ зоны (TOP_LEFT, TOP_RIGHT, BOTTOM_LEFT, BOTTOM_RIGHT, header, footer)
 * @param {boolean} visible - Видимость элемента
 * @returns {React.RefObject} ref для привязки к элементу
 */
export default function useRegisterOccupiedZone(zoneKey, visible = true) {
  const context = usePlayerOccupiedZones();
  const elementRef = useRef(null);
  const resizeObserverRef = useRef(null);

  useEffect(() => {
    // Если провайдера нет, ничего не делаем
    if (!context) {
      return;
    }

    const { registerZone, unregisterZone } = context;

    if (!elementRef.current || !visible) {
      // Если элемент не виден, снимаем регистрацию
      if (visible === false) {
        unregisterZone(zoneKey);
      }
      return;
    }

    const element = elementRef.current;

    // Функция для обновления размеров зоны
    const updateZone = () => {
      const rect = element.getBoundingClientRect();
      registerZone(zoneKey, {
        width: rect.width,
        height: rect.height,
        visible: true,
      });
    };

    // Создаем ResizeObserver для отслеживания изменений размеров
    if (typeof ResizeObserver !== "undefined") {
      resizeObserverRef.current = new ResizeObserver(() => {
        updateZone();
      });
      resizeObserverRef.current.observe(element);
    }

    // Первоначальное обновление
    updateZone();

    // Очистка при размонтировании
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      unregisterZone(zoneKey);
    };
  }, [zoneKey, visible]);

  return elementRef;
}
